import React, { useEffect, useState } from "react";
import Layout from "../components/app/Layout";
import MarketplaceProfileFields from "../components/app/MarketplaceProfileFields";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import PhotoUpload from "../components/app/PhotoUpload";
import VerificationUploader from "../components/app/VerificationUploader";
import { SPECIALTIES, SPECIALTY_LABEL } from "./FindCoaches";
import LocationPicker from "../components/app/LocationPicker";
import {
  Save, ShieldCheck, Award, Star, MapPin, Clock, Phone, Sparkles, User, Trophy, Settings as SettingsIcon,
  ImagePlus, Languages, X, Plus, Zap, Trash2, Check,
} from "lucide-react";
import { toast } from "sonner";

const BADGES = [
  { id: "uefa_a", label: "UEFA A", icon: ShieldCheck },
  { id: "uefa_b", label: "UEFA B", icon: ShieldCheck },
  { id: "uefa_pro", label: "UEFA PRO", icon: ShieldCheck },
  { id: "elite", label: "Elite", icon: Award },
  { id: "verified", label: "Verified", icon: ShieldCheck },
];

const SECTIONS = [
  { id: "profile",     label_bg: "Профил",       label_en: "Profile",       icon: User },
  { id: "details",     label_bg: "Детайли",      label_en: "Details",       icon: SettingsIcon },
  { id: "contact",     label_bg: "Контакт",      label_en: "Contact",       icon: Phone },
  { id: "payouts",     label_bg: "Плащания",     label_en: "Payouts",       icon: Trophy },
  { id: "instant",     label_bg: "Instant Book", label_en: "Instant Book",  icon: Zap },
  { id: "credentials", label_bg: "Сертификати",  label_en: "Credentials",   icon: Trophy },
  { id: "showcase",    label_bg: "Показване",    label_en: "Showcase",      icon: ImagePlus },
  { id: "verify",      label_bg: "Верификация",  label_en: "Verification",  icon: ShieldCheck },
  { id: "location",    label_bg: "Локация",      label_en: "Location",      icon: MapPin },
];

export default function CoachSettings() {
  const { user, setUser } = useAuth();
  const { t, lang } = useI18n();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState("profile");

  useEffect(() => {
    api.get("/auth/me").then((r) => setForm(r.data));
  }, []);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", {
        name: form.name,
        bio: form.bio,
        city: form.city,
        specialty: form.specialty,
        hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : null,
        available_hours: form.available_hours,
        photo_url: form.photo_url,
        phone: form.phone,
        instagram: form.instagram,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        badges: form.badges || [],
        location_lat: form.location_lat ?? null,
        location_lng: form.location_lng ?? null,
        gallery_urls: form.gallery_urls || [],
        languages_spoken: form.languages_spoken || [],
        achievements: form.achievements || [],
        academy_name: form.academy_name,
        academy_logo: form.academy_logo,
        academy_banner: form.academy_banner,
        academy_description: form.academy_description,
        academy_size: form.academy_size,
        instant_book_enabled: !!form.instant_book_enabled,
        instant_book_slots: form.instant_book_slots || [],
        // Iter 24 — contact + payouts
        whatsapp: form.whatsapp,
        viber: form.viber,
        accepts_cash: !!form.accepts_cash,
        accepts_card: form.accepts_card !== false,  // default true
        payout_iban: form.payout_iban,
        payout_bank_name: form.payout_bank_name,
        payout_account_holder: form.payout_account_holder,
      });
      if (setUser) setUser(data);
      toast.success(t("saved"));
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Error");
    }
    setSaving(false);
  };

  const f = (k) => (v) => setForm({ ...form, [k]: v });

  return (
    <Layout>
      <div className="max-w-6xl space-y-6">
        {["coach", "academy"].includes(user?.role) && <MarketplaceProfileFields role={user.role} />}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono mb-1">// {lang === "bg" ? "Настройки" : "Settings"}</div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">
              {lang === "bg" ? "Профил на треньор" : "Coach profile"}
            </h1>
          </div>
          <button type="button" onClick={save} disabled={saving} className="btn-primary px-5 py-2.5 flex items-center gap-2" data-testid="save-profile-top">
            <Save className="w-4 h-4" /> {saving ? "..." : t("save")}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Left column: form sections */}
          <form onSubmit={save} className="space-y-5">
            {/* Section tabs */}
            <div className="flex flex-wrap gap-2 border-b border-white/8 pb-3">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActive(s.id)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 transition-colors ${
                    active === s.id ? "text-pitch border-b-2 border-pitch -mb-3 pb-3" : "text-white/45 hover:text-white"
                  }`}
                  data-testid={`settings-tab-${s.id}`}
                >
                  <s.icon className="w-3.5 h-3.5" /> {lang === "bg" ? s.label_bg : s.label_en}
                </button>
              ))}
            </div>

            {/* Profile */}
            {active === "profile" && (
              <div className="surface-elevated p-6 flex flex-col sm:flex-row gap-6 items-start animate-fade-up" style={{ opacity: 0 }}>
                <div className="flex flex-col items-center gap-2">
                  <PhotoUpload value={form.photo_url} onChange={f("photo_url")} size={140} testId="coach-photo" />
                  <span className="label-mono">{lang === "bg" ? "Профилна снимка" : "Profile photo"}</span>
                </div>
                <div className="flex-1 space-y-3 w-full">
                  <Field label={t("name")} v={form.name} on={f("name")} testId="set-name" />
                  <Field label={lang === "bg" ? "Град" : "City"} v={form.city} on={f("city")} testId="set-city" icon={MapPin} />
                  <div>
                    <label className="label-mono block mb-1.5">{lang === "bg" ? "Специалност" : "Specialty"}</label>
                    <select
                      value={form.specialty || ""}
                      onChange={(e) => f("specialty")(e.target.value)}
                      className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none"
                      data-testid="set-specialty"
                    >
                      <option value="">—</option>
                      {SPECIALTIES.map((s) => (
                        <option key={s.id} value={s.id}>{lang === "bg" ? s.label_bg : s.label_en}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Details */}
            {active === "details" && (
              <div className="surface-elevated p-6 space-y-3 animate-fade-up" style={{ opacity: 0 }}>
                <div>
                  <label className="label-mono block mb-1.5">{lang === "bg" ? "Кратко описание" : "Bio"}</label>
                  <textarea
                    value={form.bio || ""}
                    onChange={(e) => f("bio")(e.target.value)}
                    rows={5}
                    className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none"
                    data-testid="set-bio"
                    placeholder={lang === "bg" ? "Опишете опит, постижения и подход..." : "Describe your experience, achievements and approach..."}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={lang === "bg" ? "Цена (€/час)" : "Hourly rate (€)"} type="number" v={form.hourly_rate} on={f("hourly_rate")} testId="set-rate" icon={Sparkles} />
                  <Field label={lang === "bg" ? "Опит (години)" : "Experience (years)"} type="number" v={form.years_experience} on={f("years_experience")} testId="set-exp" icon={Trophy} />
                  <Field label={lang === "bg" ? "Налични часове" : "Available hours"} v={form.available_hours} on={f("available_hours")} testId="set-hours" icon={Clock} placeholder="Mon-Fri 16:00–20:00" />
                  <Field label={lang === "bg" ? "Телефон" : "Phone"} v={form.phone} on={f("phone")} testId="set-phone" icon={Phone} placeholder="+359 88 ..." />
                  <Field label="Instagram" v={form.instagram} on={f("instagram")} testId="set-instagram" icon={ImagePlus} placeholder="@onthepitch" />
                </div>
              </div>
            )}

            {/* Credentials */}
            {active === "credentials" && (
              <div className="surface-elevated p-6 space-y-3 animate-fade-up" style={{ opacity: 0 }}>
                <p className="text-xs text-white/50 font-mono">
                  {lang === "bg"
                    ? "Отметни сертификатите, които притежаваш. „Top Rated“ се присъжда автоматично при ≥4.5★ и ≥10 ревюта."
                    : "Toggle the certifications you hold. „Top Rated“ is awarded automatically at ≥4.5★ and ≥10 reviews."}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {BADGES.map((b) => {
                    const active = (form.badges || []).includes(b.id);
                    return (
                      <button
                        type="button"
                        key={b.id}
                        onClick={() => {
                          const cur = form.badges || [];
                          const next = active ? cur.filter((x) => x !== b.id) : [...cur, b.id];
                          f("badges")(next);
                        }}
                        className={`py-2.5 px-3 border font-oswald uppercase tracking-wider text-xs flex items-center justify-center gap-1.5 transition-all ${
                          active
                            ? "border-pitch bg-pitch text-white glow-emerald-soft"
                            : "border-white/10 text-white/55 hover:border-pitch/50 hover:text-white"
                        }`}
                        data-testid={`badge-toggle-${b.id}`}
                      >
                        <b.icon className="w-3.5 h-3.5" /> {b.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Showcase — gallery + languages + achievements */}
            {active === "showcase" && (
              <div className="surface-elevated p-6 space-y-6 animate-fade-up" style={{ opacity: 0 }} data-testid="set-showcase">
                <p className="text-xs text-white/50 font-mono">
                  {lang === "bg"
                    ? "Покажи се на родителите: добави галерия, езици, които говориш и ключови постижения."
                    : "Stand out to parents: add a gallery, languages you speak, and key achievements."}
                </p>

                {/* Gallery */}
                <div>
                  <div className="label-mono mb-2 flex items-center gap-1.5"><ImagePlus className="w-3 h-3 text-pitch" /> {lang === "bg" ? "// Галерия (макс 12)" : "// Gallery (max 12)"}</div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {(form.gallery_urls || []).map((url, i) => (
                      <div key={i} className="relative aspect-square overflow-hidden border border-white/10 rounded">
                        <img
                          src={url + (url.startsWith("/api") ? `?auth=${localStorage.getItem("auth_token")}` : "")}
                          alt="" className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => f("gallery_urls")((form.gallery_urls || []).filter((_, j) => j !== i))}
                          className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white rounded-full flex items-center justify-center hover:bg-red-500/85"
                          data-testid={`gallery-remove-${i}`}
                          aria-label="Remove"
                        ><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {(form.gallery_urls || []).length < 12 && (
                      <div className="aspect-square">
                        <PhotoUpload
                          value={null}
                          onChange={(url) => f("gallery_urls")([...(form.gallery_urls || []), url])}
                          size={88}
                          testId="gallery-upload"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Languages spoken */}
                <ChipListEditor
                  label={lang === "bg" ? "Езици" : "Languages spoken"}
                  icon={Languages}
                  values={form.languages_spoken || []}
                  onChange={(v) => f("languages_spoken")(v)}
                  placeholder={lang === "bg" ? "напр. Английски" : "e.g. English"}
                  testId="languages"
                  max={8}
                />

                {/* Achievements */}
                <ChipListEditor
                  label={lang === "bg" ? "Постижения" : "Achievements"}
                  icon={Award}
                  values={form.achievements || []}
                  onChange={(v) => f("achievements")(v)}
                  placeholder={lang === "bg" ? "напр. Шампион 2024" : "e.g. 2024 League champion"}
                  testId="achievements"
                  max={10}
                  longText
                />
              </div>
            )}

            {/* Verification documents */}
            {active === "verify" && (
              <div className="surface-elevated p-6 space-y-4 animate-fade-up" style={{ opacity: 0 }} data-testid="set-verify">
                <div>
                  <h3 className="font-oswald uppercase text-xl tracking-tight flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-pitch" />
                    {lang === "bg" ? "Документи за верификация" : "Verification documents"}
                  </h3>
                  <p className="text-xs text-white/50 font-mono mt-2 leading-relaxed">
                    {lang === "bg"
                      ? "Качи UEFA сертификат и ID, за да получиш Verified значка от ONTHEPITCH. Документите се преглеждат от админ екипа и са поверителни."
                      : "Upload your UEFA certificate and ID to earn the ONTHEPITCH Verified badge. Documents are reviewed by our admin team and remain confidential."}
                  </p>
                </div>
                <VerificationUploader lang={lang} value={form.verification_docs || []} onChange={(d) => setForm((p) => ({ ...p, verification_docs: d }))} />
              </div>
            )}

            {/* Location */}
            {active === "location" && (
              <div className="surface-elevated p-6 space-y-3 animate-fade-up" style={{ opacity: 0 }}>
                <p className="text-xs text-white/50 font-mono">
                  {lang === "bg"
                    ? "Маркирай локацията, на която тренираш — родители ще те виждат на картата „Треньори около теб“."
                    : "Pin where you train — parents will discover you on the „Coaches near you“ map."}
                </p>
                <LocationPicker
                  lat={typeof form.location_lat === "number" ? form.location_lat : null}
                  lng={typeof form.location_lng === "number" ? form.location_lng : null}
                  onChange={(pos) => {
                    setForm((p) => ({ ...p, location_lat: pos?.lat ?? null, location_lng: pos?.lng ?? null }));
                  }}
                />
                <div className="flex items-center gap-3 text-xs font-mono">
                  <span className="label-mono">LAT</span>
                  <span className="text-white/55">{form.location_lat?.toFixed?.(5) ?? "—"}</span>
                  <span className="label-mono ml-3">LNG</span>
                  <span className="text-white/55">{form.location_lng?.toFixed?.(5) ?? "—"}</span>
                  {(form.location_lat || form.location_lng) && (
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, location_lat: null, location_lng: null }))}
                      className="ml-auto text-red-400 hover:text-red-300"
                      data-testid="clear-location"
                    >
                      {lang === "bg" ? "Изчисти" : "Clear"}
                    </button>
                  )}
                </div>
                {/* Iter 25 — manual refresh from city */}
                {form.city && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/auth/refresh-location");
                        setForm((p) => ({ ...p, location_lat: data.lat, location_lng: data.lng }));
                        toast.success(lang === "bg" ? "Локацията е обновена" : "Location refreshed");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || (lang === "bg" ? "Грешка" : "Error"));
                      }
                    }}
                    className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-1.5"
                    data-testid="refresh-location-btn"
                  >
                    <MapPin className="w-3.5 h-3.5" /> {lang === "bg" ? `Обнови от „${form.city}"` : `Refresh from "${form.city}"`}
                  </button>
                )}
              </div>
            )}

            {/* Contact section — Iter 24 */}
            {active === "contact" && (
              <section className="surface-elevated p-5 sm:p-6 space-y-5 rounded-lg" data-testid="contact-section">
                <div>
                  <div className="label-mono mb-1">// {lang === "bg" ? "Контакт" : "Contact"}</div>
                  <h2 className="font-oswald uppercase text-xl sm:text-2xl tracking-tight leading-tight">
                    {lang === "bg" ? "Как родителите се свързват" : "How parents reach you"}
                  </h2>
                  <p className="text-xs text-white/55 mt-1.5">
                    {lang === "bg"
                      ? "Тези линкове се появяват в профила ти СЛЕД като родителят направи резервация (за защита на личните данни)."
                      : "These links appear on your profile only AFTER a parent makes a booking (privacy-safe)."}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={lang === "bg" ? "Телефон" : "Phone"} v={form.phone} on={f("phone")} placeholder="+359..." icon={Phone} testId="contact-phone" />
                  <Field label="WhatsApp" v={form.whatsapp} on={f("whatsapp")} placeholder="+359..." icon={Phone} testId="contact-whatsapp" />
                  <Field label="Viber" v={form.viber} on={f("viber")} placeholder="+359..." icon={Phone} testId="contact-viber" />
                  <Field label="Instagram" v={form.instagram} on={f("instagram")} placeholder="@handle" icon={Sparkles} testId="contact-instagram" />
                </div>
              </section>
            )}

            {/* Payouts section — Iter 24 */}
            {active === "payouts" && (
              <section className="surface-elevated p-5 sm:p-6 space-y-5 rounded-lg" data-testid="payouts-section">
                <div>
                  <div className="label-mono mb-1">// {lang === "bg" ? "Плащания и Изплащания" : "Payments & Payouts"}</div>
                  <h2 className="font-oswald uppercase text-xl sm:text-2xl tracking-tight leading-tight">
                    {lang === "bg" ? "Как получаваш парите си" : "How you get paid"}
                  </h2>
                </div>

                <div className="border border-white/8 rounded-lg p-4 space-y-3">
                  <div className="label-mono">// {lang === "bg" ? "Приемани методи" : "Accepted methods"}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <ToggleCard
                      label={lang === "bg" ? "Карта" : "Card"}
                      desc={lang === "bg" ? "Онлайн плащане през Stripe" : "Online via Stripe"}
                      active={form.accepts_card !== false}
                      onToggle={() => setForm((p) => ({ ...p, accepts_card: !(p.accepts_card !== false) }))}
                      testId="toggle-accepts-card"
                    />
                    <ToggleCard
                      label={lang === "bg" ? "В брой" : "Cash"}
                      desc={lang === "bg" ? "Плащане на място" : "Pay on training"}
                      active={!!form.accepts_cash}
                      onToggle={() => setForm((p) => ({ ...p, accepts_cash: !p.accepts_cash }))}
                      testId="toggle-accepts-cash"
                    />
                  </div>
                </div>

                <div className="border border-white/8 rounded-lg p-4 space-y-3">
                  <div>
                    <div className="label-mono">// {lang === "bg" ? "Банкова сметка за изплащания" : "Bank account for payouts"}</div>
                    <p className="text-xs text-white/55 mt-1">
                      {lang === "bg"
                        ? "ONTHEPITCH ще ти превежда натрупаните плащания по тази сметка веднъж седмично."
                        : "ONTHEPITCH transfers accumulated card payments to this account weekly."}
                    </p>
                  </div>
                  <Field label={lang === "bg" ? "Име на титуляр" : "Account holder"} v={form.payout_account_holder} on={f("payout_account_holder")} testId="payout-holder" />
                  <Field label="IBAN" v={form.payout_iban} on={f("payout_iban")} placeholder="BG..." testId="payout-iban" />
                  <Field label={lang === "bg" ? "Банка" : "Bank name"} v={form.payout_bank_name} on={f("payout_bank_name")} testId="payout-bank" />
                  {form.payout_iban && form.payout_account_holder && (
                    <div className="flex items-center gap-2 text-xs text-pitch font-mono pt-1" data-testid="payout-ready">
                      <Check className="w-3.5 h-3.5" /> {lang === "bg" ? "Готов за изплащания" : "Ready for payouts"}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Instant Book section */}
            {active === "instant" && (
              <InstantBookSection form={form} setForm={setForm} lang={lang} />
            )}

            <div className="flex justify-end">
              <button type="submit" disabled={saving} className="btn-primary px-6 py-3 flex items-center gap-2" data-testid="save-profile">
                <Save className="w-4 h-4" /> {t("save")}
              </button>
            </div>
          </form>

          {/* Right column: live preview */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="label-mono mb-2">// {lang === "bg" ? "Преглед" : "Preview"}</div>
            <div className="card-coach p-5">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 flex-shrink-0 relative">
                  {form.photo_url ? (
                    <img src={form.photo_url + (form.photo_url.startsWith("/api") ? `?auth=${localStorage.getItem("auth_token")}` : "")}
                         alt={form.name}
                         className="w-full h-full object-cover rounded-full border-2 border-pitch/40" />
                  ) : (
                    <div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-2xl">{form.name?.[0] || "C"}</div>
                  )}
                  {(form.badges || []).includes("verified") && (
                    <span className="absolute -bottom-1 -right-1 w-6 h-6 bg-pitch text-white rounded-full flex items-center justify-center text-[11px] font-bold border-2 border-[#0A1628]" title="Verified">✓</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-oswald uppercase text-lg tracking-tight truncate">{form.name || "—"}</div>
                  <div className="flex items-center gap-2 text-xs text-white/55 mt-1">
                    <Star className="w-3 h-3 text-pitch fill-pitch" />
                    <span className="font-mono text-white">{(form.rating || 0).toFixed(1)}</span>
                    <span className="text-white/25">·</span>
                    <span className="font-mono">{form.review_count || 0} {lang === "bg" ? "оценки" : "reviews"}</span>
                  </div>
                  {form.city && (
                    <div className="text-xs text-white/40 flex items-center gap-1 mt-1 font-mono">
                      <MapPin className="w-3 h-3 text-pitch" /> {form.city}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {form.specialty && <span className="chip-badge">{SPECIALTY_LABEL(form.specialty, lang)}</span>}
                {(form.badges || []).slice(0, 3).map((b) => (
                  <span key={b} className={`chip-badge ${b === "uefa_pro" ? "gold" : b === "top_rated" ? "amber" : b === "elite" ? "platinum" : ""}`}>
                    {b.toUpperCase().replace("_", " ")}
                  </span>
                ))}
                {form.years_experience != null && form.years_experience !== "" && <span className="chip-badge">{form.years_experience}+ {lang === "bg" ? "г." : "yr"}</span>}
              </div>
              {form.bio && <p className="text-xs text-white/55 mt-3 line-clamp-3">{form.bio}</p>}
              <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
                <span className="label-mono">{lang === "bg" ? "От" : "From"}</span>
                <span className="font-oswald text-2xl text-pitch text-glow-emerald">
                  {form.hourly_rate ? `€${form.hourly_rate}` : "—"}
                  {form.hourly_rate && <span className="text-xs text-white/40 ml-1">/{lang === "bg" ? "ч" : "h"}</span>}
                </span>
              </div>
              {form.available_hours && (
                <div className="mt-2 text-xs text-white/40 truncate font-mono">⏱ {form.available_hours}</div>
              )}
            </div>
            <p className="text-[10px] font-mono text-white/30 mt-2 text-center">
              {lang === "bg" ? "Така изглежда твоят профил в „Открий треньор“" : "This is how your card appears in „Discover coaches“"}
            </p>
          </aside>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, v, on, type = "text", testId, icon: Icon, placeholder }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />}
        <input
          type={type}
          value={v ?? ""}
          placeholder={placeholder}
          onChange={(e) => on(e.target.value)}
          className={`w-full bg-[#0A1628] border border-white/10 ${Icon ? "pl-9" : "px-3"} pr-3 py-2.5 focus:border-pitch outline-none`}
          data-testid={testId}
        />
      </div>
    </div>
  );
}

function ChipListEditor({ label, icon: Icon, values, onChange, placeholder, testId, max = 8, longText = false }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = (draft || "").trim();
    if (!v || values.includes(v) || values.length >= max) return;
    onChange([...values, v]);
    setDraft("");
  };
  const remove = (i) => onChange(values.filter((_, j) => j !== i));
  return (
    <div>
      <div className="label-mono mb-2 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3 text-pitch" />} {`// ${label}`} <span className="text-white/30">({values.length}/{max})</span>
      </div>
      <div className="flex gap-2 mb-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none"
          data-testid={`${testId}-input`}
        />
        <button type="button" onClick={add} disabled={!draft.trim() || values.length >= max} className="btn-primary px-3 py-2.5 text-xs flex items-center gap-1 disabled:opacity-40" data-testid={`${testId}-add`}>
          <Plus className="w-3 h-3" /> {longText ? "Add" : ""}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v, i) => (
          <span key={i} className="chip-badge flex items-center gap-1.5" data-testid={`${testId}-chip-${i}`}>
            {v}
            <button type="button" onClick={() => remove(i)} className="text-white/40 hover:text-red-400" aria-label="Remove"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}


const WEEKDAYS_BG = ["Понеделник", "Вторник", "Сряда", "Четвъртък", "Петък", "Събота", "Неделя"];
const WEEKDAYS_EN = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DURATIONS = [30, 45, 60, 75, 90, 105, 120, 150, 180];

function InstantBookSection({ form, setForm, lang }) {
  const slots = form.instant_book_slots || [];
  const enabled = !!form.instant_book_enabled;
  const [draft, setDraft] = React.useState({ weekday: 1, hour: 18, minute: 0, duration_min: 60 });
  const wkLabels = lang === "bg" ? WEEKDAYS_BG : WEEKDAYS_EN;

  const setEnabled = (v) => setForm((p) => ({ ...p, instant_book_enabled: v }));
  const removeSlot = (idx) => setForm((p) => ({ ...p, instant_book_slots: slots.filter((_, i) => i !== idx) }));
  const addSlot = () => {
    if (slots.length >= 14) {
      toast.error(lang === "bg" ? "Максимум 14 слота" : "Max 14 slots");
      return;
    }
    const exists = slots.find((s) => s.weekday === draft.weekday && s.hour === draft.hour && s.minute === draft.minute);
    if (exists) {
      toast.error(lang === "bg" ? "Този слот вече съществува" : "Slot already exists");
      return;
    }
    setForm((p) => ({ ...p, instant_book_slots: [...slots, { ...draft }].sort((a, b) => a.weekday - b.weekday || a.hour - b.hour || a.minute - b.minute) }));
  };

  const sortedSlots = [...slots].sort((a, b) => a.weekday - b.weekday || a.hour - b.hour || a.minute - b.minute);

  return (
    <section className="surface-elevated p-5 sm:p-6 space-y-5 rounded-lg" data-testid="instant-book-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center flex-shrink-0">
            <Zap className="w-4 h-4 text-gold" style={{ color: "#FFC857" }} />
          </div>
          <div className="min-w-0">
            <div className="label-mono mb-1">// Instant Book</div>
            <h2 className="font-oswald uppercase text-xl sm:text-2xl tracking-tight leading-tight">
              {lang === "bg" ? "Авто-потвърждавани сесии" : "Auto-confirmed sessions"}
            </h2>
            <p className="text-xs text-white/55 mt-1.5 max-w-xl">
              {lang === "bg"
                ? "Маркирай повтарящи се слотове в седмицата. Резервациите за тях ще се потвърждават автоматично след плащане — без чакане от твоя страна."
                : "Mark recurring weekly slots. Bookings for these slots auto-confirm the moment payment lands — no manual approval needed."}
            </p>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none flex-shrink-0" data-testid="instant-book-toggle">
          <span className="text-xs font-mono uppercase tracking-widest text-white/55">
            {enabled ? (lang === "bg" ? "Активно" : "Active") : (lang === "bg" ? "Изключено" : "Off")}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label={lang === "bg" ? "Активирай Instant Book" : "Enable Instant Book"}
            onClick={() => setEnabled(!enabled)}
            className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? "bg-pitch" : "bg-white/15"}`}
            data-testid="instant-book-switch"
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-0.5"}`} />
          </button>
        </label>
      </div>

      {enabled && (
        <>
          {/* Add slot row */}
          <div className="border border-pitch/25 bg-pitch/[0.03] rounded-lg p-4">
            <div className="label-mono mb-3">// {lang === "bg" ? "Добави слот" : "Add a slot"}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/45 mb-1.5">{lang === "bg" ? "Ден" : "Day"}</label>
                <select
                  value={draft.weekday}
                  onChange={(e) => setDraft({ ...draft, weekday: Number(e.target.value) })}
                  className="w-full bg-[#0A1628] border border-white/10 px-3 py-2 focus:border-pitch outline-none rounded-md text-sm"
                  data-testid="instant-draft-weekday"
                >
                  {wkLabels.map((w, i) => <option key={i} value={i}>{w}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/45 mb-1.5">{lang === "bg" ? "Час" : "Hour"}</label>
                <select
                  value={draft.hour}
                  onChange={(e) => setDraft({ ...draft, hour: Number(e.target.value) })}
                  className="w-full bg-[#0A1628] border border-white/10 px-3 py-2 focus:border-pitch outline-none rounded-md text-sm"
                  data-testid="instant-draft-hour"
                >
                  {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, "0")}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/45 mb-1.5">{lang === "bg" ? "Минути" : "Minutes"}</label>
                <select
                  value={draft.minute}
                  onChange={(e) => setDraft({ ...draft, minute: Number(e.target.value) })}
                  className="w-full bg-[#0A1628] border border-white/10 px-3 py-2 focus:border-pitch outline-none rounded-md text-sm"
                  data-testid="instant-draft-minute"
                >
                  {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/45 mb-1.5">{lang === "bg" ? "Продълж." : "Duration"}</label>
                <select
                  value={draft.duration_min}
                  onChange={(e) => setDraft({ ...draft, duration_min: Number(e.target.value) })}
                  className="w-full bg-[#0A1628] border border-white/10 px-3 py-2 focus:border-pitch outline-none rounded-md text-sm"
                  data-testid="instant-draft-duration"
                >
                  {DURATIONS.map((d) => <option key={d} value={d}>{d} {lang === "bg" ? "мин" : "min"}</option>)}
                </select>
              </div>
            </div>
            <button type="button" onClick={addSlot} className="btn-primary px-4 py-2 mt-3 flex items-center gap-2 text-sm" data-testid="instant-add-slot">
              <Plus className="w-3.5 h-3.5" /> {lang === "bg" ? "Добави" : "Add slot"}
            </button>
          </div>

          {/* Existing slots */}
          {sortedSlots.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-white/10 rounded-md" data-testid="instant-empty">
              <Zap className="w-6 h-6 mx-auto mb-2 text-white/25" />
              <div className="text-sm text-white/55">{lang === "bg" ? "Все още нямаш слотове" : "No slots yet"}</div>
              <div className="text-[11px] text-white/35 mt-1">{lang === "bg" ? "Добави поне един за да активираш Instant Book" : "Add at least one to activate Instant Book"}</div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="label-mono">// {lang === "bg" ? "Активни слотове" : "Active slots"} ({sortedSlots.length})</div>
              {sortedSlots.map((s, i) => (
                <div key={`${s.weekday}-${s.hour}-${s.minute}`} className="flex items-center justify-between gap-3 border border-white/8 hover:border-pitch/30 transition-colors rounded-md p-3" data-testid={`instant-slot-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="chip-badge gold flex items-center gap-1.5 flex-shrink-0">
                      <Zap className="w-3 h-3" /> Instant
                    </span>
                    <div className="min-w-0">
                      <div className="font-oswald uppercase tracking-tight text-sm">{wkLabels[s.weekday]}</div>
                      <div className="text-xs text-white/55 font-mono">
                        {String(s.hour).padStart(2, "0")}:{String(s.minute).padStart(2, "0")} · {s.duration_min} {lang === "bg" ? "мин" : "min"}
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeSlot(i)} className="text-white/35 hover:text-red-400 transition-colors p-1.5 rounded-md" aria-label="Remove slot" data-testid={`instant-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}


function ToggleCard({ label, desc, active, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`text-left p-3 border-2 rounded-md transition-all ${active ? "border-pitch bg-pitch/8" : "border-white/10 hover:border-white/25"}`}
      data-testid={testId}
      aria-pressed={active}
    >
      <div className="flex items-center justify-between">
        <span className="font-oswald uppercase tracking-tight text-base">{label}</span>
        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? "bg-pitch border-pitch" : "border-white/25"}`}>
          {active && <span className="w-1.5 h-1.5 rounded-full bg-black" />}
        </span>
      </div>
      <div className="text-[11px] text-white/55 mt-1">{desc}</div>
    </button>
  );
}
